'use client'

import React from 'react'
import { collectionSmartTagAliases } from './collection-tag'
import useTwetchFeed, { type TwetchFeedData } from './use-twetch-feed'

export function mergeCollectionFeeds(feeds: TwetchFeedData[]): TwetchFeedData {
	const posts = [
		...new Map(feeds.flatMap((feed) => feed.posts).map((post) => [post.id, post])).values()
	].sort((a, b) => (b.createdAtMs ?? b.postedAtMs ?? 0) - (a.createdAtMs ?? a.postedAtMs ?? 0))
	const users = Object.assign({}, ...feeds.map((feed) => feed.users))
	const replyPosts = Object.assign({}, ...feeds.map((feed) => feed.replyPosts))
	const quotes = Object.assign({}, ...feeds.map((feed) => feed.quotes))
	const quotedPosts = Object.assign({}, ...feeds.map((feed) => feed.quotedPosts))
	const mediaDims = Object.assign({}, ...feeds.map((feed) => feed.mediaDims))
	const loadMore = () => feeds.forEach((feed) => feed.hasMore && feed.loadMore())
	const retry = () => feeds.forEach((feed) => feed.retry())

	return {
		posts,
		users,
		replyPosts,
		quotes,
		quotedPosts,
		mediaDims,
		loadingInitial: posts.length === 0 && feeds.some((feed) => feed.loadingInitial),
		loadingMore: feeds.some((feed) => feed.loadingMore),
		failed: posts.length === 0 && feeds.some((feed) => feed.failed),
		hasMore: feeds.some((feed) => feed.hasMore),
		loadMore,
		retry
	}
}

/// The collection feed across its canonical slug and the separator-equivalent
/// historical slugs OW has used. Keep three fixed hook slots so React hook
/// order never changes when navigating between collections.
export default function useCollectionTwetchFeed(active: boolean, slug: string): TwetchFeedData {
	const tags = React.useMemo(() => collectionSmartTagAliases(slug), [slug])
	const primary = useTwetchFeed(active && !!tags[0], tags[0]?.id ?? null, null, tags[0]?.namespace)
	const legacyA = useTwetchFeed(active && !!tags[1], tags[1]?.id ?? null, null, tags[1]?.namespace)
	const legacyB = useTwetchFeed(active && !!tags[2], tags[2]?.id ?? null, null, tags[2]?.namespace)

	return React.useMemo(
		() => mergeCollectionFeeds(active ? [primary, legacyA, legacyB].slice(0, tags.length) : []),
		[active, tags.length, primary, legacyA, legacyB]
	)
}
